import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Search, ChevronLeft, ChevronRight, BellOff, BellRing, Upload, Download, Settings2, Copy, Archive, ArchiveRestore, Tag } from "lucide-react";
import api, { API } from "@/lib/api";
import ContactDetailDrawer from "./ContactDetailDrawer";
import ImportWizardModal from "./ImportWizardModal";
import CustomFieldsModal from "./CustomFieldsModal";
import DuplicatesModal from "./DuplicatesModal";

const ROLE_BADGE = {
  user: "bg-indigo-400/10 text-indigo-300",
  recruiter: "bg-fuchsia-400/10 text-fuchsia-300",
  admin: "bg-amber-400/10 text-amber-300",
  manual: "bg-slate-500/10 text-slate-300",
};

export default function ContactsPanel({ onContactsChanged }) {
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const limit = 20;
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [allTags, setAllTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newContact, setNewContact] = useState({ name: "", email: "", phone: "", company: "", city: "", tags: "" });
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState([]);
  const [openContactId, setOpenContactId] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [showCustomFields, setShowCustomFields] = useState(false);
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [bulkTag, setBulkTag] = useState("");

  const loadContacts = () => {
    setLoading(true);
    api.get("/admin/crm/contacts", { params: { search: search || undefined, role: roleFilter || undefined, tag: tagFilter || undefined, include_archived: includeArchived, page, limit } })
      .then((r) => { setContacts(r.data.contacts); setTotal(r.data.total); })
      .catch(() => toast.error("Could not load contacts"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    api.get("/admin/crm/contacts/tags").then((r) => setAllTags(r.data));
  }, []);

  useEffect(() => {
    loadContacts();
    setSelected([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, roleFilter, tagFilter, includeArchived]);

  const runSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadContacts();
  };

  const createContact = async () => {
    if (!newContact.name.trim() || !newContact.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setCreating(true);
    try {
      await api.post("/admin/crm/contacts", {
        name: newContact.name, email: newContact.email, phone: newContact.phone || null,
        company: newContact.company || null, city: newContact.city || null,
        tags: newContact.tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      toast.success("Contact added");
      setNewContact({ name: "", email: "", phone: "", company: "", city: "", tags: "" });
      setShowAddForm(false);
      loadContacts();
      onContactsChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add contact");
    } finally {
      setCreating(false);
    }
  };

  const toggleSubscription = async (contact) => {
    const status = contact.status === "subscribed" ? "unsubscribed" : "subscribed";
    await api.put(`/admin/crm/contacts/${contact.id}`, { status });
    setContacts((prev) => prev.map((c) => (c.id === contact.id ? { ...c, status } : c)));
    toast.success(status === "subscribed" ? "Contact resubscribed" : "Contact unsubscribed");
  };

  const deleteContact = async (id) => {
    await api.delete(`/admin/crm/contacts/${id}`);
    setContacts((prev) => prev.filter((c) => c.id !== id));
    setTotal((t) => t - 1);
    toast.success("Contact deleted");
    onContactsChanged?.();
  };

  const toggleSelect = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));
  };

  const toggleSelectAll = () => {
    setSelected(selected.length === contacts.length ? [] : contacts.map((c) => c.id));
  };

  const runBulkAction = async (action, extra = {}) => {
    try {
      const { data } = await api.post("/admin/crm/contacts/bulk", { contact_ids: selected, action, ...extra });
      toast.success(data.message);
      setSelected([]);
      loadContacts();
      onContactsChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk action failed");
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div data-testid="crm-contacts-panel">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <form onSubmit={runSearch} className="flex items-center gap-2 flex-1 min-w-[220px] flex-wrap">
          <div className="relative flex-1 max-w-xs">
            <Search className="w-3.5 h-3.5 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email, phone"
              data-testid="crm-contacts-search-input"
              className="w-full bg-white/5 border border-white/10 rounded-lg pl-8 pr-3 py-2 text-xs text-white focus:outline-none focus:ring-1 focus:ring-indigo-400" />
          </div>
          <select value={roleFilter} onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }} data-testid="crm-contacts-role-filter"
            className="bg-white/5 border border-white/10 rounded-lg px-2.5 py-2 text-xs text-slate-200">
            <option value="">All Roles</option>
            <option value="user">Learner</option>
            <option value="recruiter">Recruiter</option>
            <option value="admin">Admin</option>
            <option value="manual">Manual</option>
          </select>
          <select value={tagFilter} onChange={(e) => { setTagFilter(e.target.value); setPage(1); }} data-testid="crm-contacts-tag-filter"
            className="bg-white/5 border border-white/10 rounded-lg px-2.5 py-2 text-xs text-slate-200">
            <option value="">All Tags</option>
            {allTags.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <label className="flex items-center gap-1.5 text-xs text-slate-400">
            <input type="checkbox" checked={includeArchived} onChange={(e) => { setIncludeArchived(e.target.checked); setPage(1); }} data-testid="crm-contacts-include-archived-checkbox" />
            Show archived
          </label>
        </form>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setShowDuplicates(true)} data-testid="crm-open-duplicates-btn" title="Find Duplicates"
            className="flex items-center gap-1.5 text-xs font-semibold bg-white/5 hover:bg-white/10 text-white px-3 py-2 rounded-lg"><Copy className="w-3.5 h-3.5" /> Duplicates</button>
          <button onClick={() => setShowCustomFields(true)} data-testid="crm-open-custom-fields-btn" title="Custom Fields"
            className="flex items-center gap-1.5 text-xs font-semibold bg-white/5 hover:bg-white/10 text-white px-3 py-2 rounded-lg"><Settings2 className="w-3.5 h-3.5" /> Fields</button>
          <a href={`${API}/admin/crm/contacts/export?format=csv`} data-testid="crm-export-contacts-btn"
            className="flex items-center gap-1.5 text-xs font-semibold bg-white/5 hover:bg-white/10 text-white px-3 py-2 rounded-lg"><Download className="w-3.5 h-3.5" /> Export</a>
          <button onClick={() => setShowImport(true)} data-testid="crm-open-import-btn"
            className="flex items-center gap-1.5 text-xs font-semibold bg-white/10 hover:bg-white/15 text-white px-3 py-2 rounded-lg"><Upload className="w-3.5 h-3.5" /> Import</button>
          <button onClick={() => setShowAddForm((s) => !s)} data-testid="crm-add-contact-toggle-btn"
            className="flex items-center gap-1.5 text-xs font-semibold bg-primary hover:bg-primary/90 text-white px-3 py-2 rounded-lg">
            <Plus className="w-3.5 h-3.5" /> Add Contact
          </button>
        </div>
      </div>

      {selected.length > 0 && (
        <div className="glass-panel rounded-xl p-3 mb-4 flex items-center gap-3 flex-wrap" data-testid="crm-bulk-action-bar">
          <span className="text-xs text-slate-300">{selected.length} selected</span>
          <input value={bulkTag} onChange={(e) => setBulkTag(e.target.value)} placeholder="tag name" data-testid="crm-bulk-tag-input"
            className="bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white w-28" />
          <button onClick={() => bulkTag && runBulkAction("tag", { tag: bulkTag })} data-testid="crm-bulk-tag-btn" className="flex items-center gap-1 text-xs text-amber-300 hover:text-amber-200"><Tag className="w-3.5 h-3.5" /> Tag</button>
          <button onClick={() => runBulkAction("archive")} data-testid="crm-bulk-archive-btn" className="flex items-center gap-1 text-xs text-slate-300 hover:text-white"><Archive className="w-3.5 h-3.5" /> Archive</button>
          <button onClick={() => runBulkAction("restore")} data-testid="crm-bulk-restore-btn" className="flex items-center gap-1 text-xs text-slate-300 hover:text-white"><ArchiveRestore className="w-3.5 h-3.5" /> Restore</button>
          <button onClick={() => runBulkAction("delete")} data-testid="crm-bulk-delete-btn" className="flex items-center gap-1 text-xs text-red-400 hover:text-red-300"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
        </div>
      )}

      {showAddForm && (
        <div className="glass-panel rounded-xl p-4 mb-4 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="crm-add-contact-form">
          <input value={newContact.name} onChange={(e) => setNewContact({ ...newContact, name: e.target.value })} placeholder="Full Name"
            data-testid="crm-new-contact-name" className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs text-white" />
          <input value={newContact.email} onChange={(e) => setNewContact({ ...newContact, email: e.target.value })} placeholder="Email"
            data-testid="crm-new-contact-email" className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs text-white" />
          <input value={newContact.phone} onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })} placeholder="Phone (+91...)"
            data-testid="crm-new-contact-phone" className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs text-white" />
          <input value={newContact.company} onChange={(e) => setNewContact({ ...newContact, company: e.target.value })} placeholder="Company"
            data-testid="crm-new-contact-company" className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs text-white" />
          <input value={newContact.city} onChange={(e) => setNewContact({ ...newContact, city: e.target.value })} placeholder="City"
            data-testid="crm-new-contact-city" className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs text-white" />
          <input value={newContact.tags} onChange={(e) => setNewContact({ ...newContact, tags: e.target.value })} placeholder="Tags (comma separated)"
            data-testid="crm-new-contact-tags" className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs text-white" />
          <button onClick={createContact} disabled={creating} data-testid="crm-save-new-contact-btn"
            className="sm:col-span-3 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 text-xs font-semibold px-4 py-2 rounded-lg disabled:opacity-50">
            {creating ? "Saving…" : "Save Contact"}
          </button>
        </div>
      )}

      <div className="glass-panel rounded-2xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-slate-400 border-b border-white/10">
              <th className="p-3"><input type="checkbox" checked={selected.length === contacts.length && contacts.length > 0} onChange={toggleSelectAll} data-testid="crm-select-all-checkbox" /></th>
              <th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Company</th>
              <th className="p-3">Role</th><th className="p-3">Tags</th><th className="p-3">Status</th><th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {contacts.map((c) => (
              <tr key={c.id} className={`border-b border-white/5 last:border-0 ${c.archived ? "opacity-50" : ""}`} data-testid={`crm-contact-row-${c.id}`}>
                <td className="p-3" onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={selected.includes(c.id)} onChange={() => toggleSelect(c.id)} data-testid={`crm-select-contact-${c.id}`} /></td>
                <td className="p-3 text-white cursor-pointer hover:underline" onClick={() => setOpenContactId(c.id)} data-testid={`crm-open-contact-${c.id}`}>{c.name}</td>
                <td className="p-3 text-slate-400">{c.email}</td>
                <td className="p-3 text-slate-400">{c.company || "—"}</td>
                <td className="p-3"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${ROLE_BADGE[c.role] || ROLE_BADGE.manual}`}>{c.role}</span></td>
                <td className="p-3">
                  <div className="flex gap-1 flex-wrap max-w-[160px]">
                    {(c.tags || []).map((t) => <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-slate-300">{t}</span>)}
                  </div>
                </td>
                <td className="p-3">
                  <span className={`text-xs font-semibold px-2 py-1 rounded-full ${c.status === "subscribed" ? "bg-emerald-400/10 text-emerald-400" : "bg-slate-500/10 text-slate-400"}`}>
                    {c.archived ? "archived" : c.status}
                  </span>
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-2 justify-end">
                    <button onClick={() => toggleSubscription(c)} title={c.status === "subscribed" ? "Unsubscribe" : "Resubscribe"}
                      data-testid={`crm-toggle-sub-btn-${c.id}`} className="text-slate-400 hover:text-white">
                      {c.status === "subscribed" ? <BellOff className="w-3.5 h-3.5" /> : <BellRing className="w-3.5 h-3.5" />}
                    </button>
                    <button onClick={() => deleteContact(c.id)} data-testid={`crm-delete-contact-btn-${c.id}`} className="text-red-400 hover:text-red-300">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {!loading && contacts.length === 0 && (
              <tr><td colSpan={8} className="p-6 text-center text-slate-400 text-sm" data-testid="crm-no-contacts">No contacts found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {total > limit && (
        <div className="flex items-center justify-center gap-3 mt-4">
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} data-testid="crm-contacts-prev-page"
            className="text-slate-400 hover:text-white disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
          <span className="text-xs text-slate-400">Page {page} of {totalPages}</span>
          <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} data-testid="crm-contacts-next-page"
            className="text-slate-400 hover:text-white disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
        </div>
      )}

      {openContactId && <ContactDetailDrawer contactId={openContactId} onClose={() => setOpenContactId(null)} />}
      {showImport && <ImportWizardModal onClose={() => setShowImport(false)} onImported={() => { loadContacts(); onContactsChanged?.(); }} />}
      {showCustomFields && <CustomFieldsModal onClose={() => setShowCustomFields(false)} />}
      {showDuplicates && <DuplicatesModal onClose={() => setShowDuplicates(false)} onMerged={loadContacts} />}
    </div>
  );
}
